import * as PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { GoogleMap } from 'react-google-maps';

import config from 'config/index';

import MapLoader from '../MapLoader';

import SearchBox from '../../containers/SearchBox';
import Marker from '../../containers/Marker';

import { MarkerTypes } from '../../services/constants';

const Element = <div style={{ height: '100%' }} />;

export const ContainerElement = Element;
export const MapElement = Element;
export const LoadingElement = <MapLoader />;

const pinShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    title: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.oneOf(Object.values(MarkerTypes)).isRequired,
});

const useMapLogic = (newPin, activePinId, pins) => {
    const mapRef = useRef();

    const fitBounds = () => {
        const google = window.google;
        const map = mapRef.current;
        const bounds = new google.maps.LatLngBounds();
        pins.forEach(pin => {
            const position = new google.maps.LatLng(pin.lat, pin.lng);
            bounds.extend(position);
        });
        map.fitBounds(bounds);
    };

    const panToPin = id => {
        const google = window.google;
        const map = mapRef.current;
        const pin = pins.find(p => p.id === id);
        if (pin) {
            const position = new google.maps.LatLng(pin.lat, pin.lng);
            map.panTo(position);
        }
    };

    const panNewPin = () => {
        const google = window.google;
        const map = mapRef.current;
        if (newPin) {
            const position = new google.maps.LatLng(newPin.lat, newPin.lng);
            map.panTo(position);
        }
    };

    useEffect(() => {
        if (!activePinId) {
            fitBounds();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activePinId) {
            panToPin(activePinId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePinId]);

    useEffect(() => {
        if (newPin && newPin.searched) {
            panNewPin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newPin]);
    return mapRef;
};

const Map = ({ newPin, activePinId, handleClick, pins }) => {
    const [openInfoWindowId, setOpenInfoWindowId] = useState(null);
    const mapRef = useMapLogic(newPin, activePinId, pins);

    return (
        <GoogleMap ref={mapRef} onClick={handleClick}>
            <SearchBox />
            {newPin && (
                <Marker
                    position={{
                        lat: newPin.lat,
                        lng: newPin.lng,
                    }}
                    type={MarkerTypes.PIN}
                />
            )}
            {pins.map(({ lat, lng, id, title, name, type }) => (
                <Marker
                    key={id}
                    id={id}
                    title={title || name}
                    position={{
                        lat,
                        lng,
                    }}
                    type={type}
                    link={config.routes.pinsDetail.replace(':id', id)}
                    openInfoWindow={id === openInfoWindowId}
                    onClick={() => {
                        setOpenInfoWindowId(id);
                    }}
                    onCloseClick={() => {
                        setOpenInfoWindowId(null);
                    }}
                />
            ))}
        </GoogleMap>
    );
};

Map.propTypes = {
    handleClick: PropTypes.func.isRequired,
    newPin: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
    }),
    pins: PropTypes.arrayOf(pinShape).isRequired,
    activePinId: PropTypes.string,
};

Map.defaultProps = {
    activePinId: null,
    newPin: null,
};

export default Map;
